/**
 * How a subscription re-check shows itself (review of #3988, 2026-09-26).
 *
 * Account re-reads the subscription on every visit (the 7-day clock can start on a
 * chat turn). A re-check that flipped `isLoading` blanked Account's one-liner and
 * turned its button into the generic "Manage Subscription" for the length of the
 * fetch — and a tap in that window routed by the LOADING surface: an expired user or
 * a lapsed payer landed on the upgrade paywall's "You're on a free trial…", a
 * pre-trial clock user on a paywall instead of chat.
 *
 * A BACKGROUND re-check of a status the screen already shows keeps showing it until
 * the answer arrives, and a failed one keeps it (a network blip must not repaint a
 * lapsed payer as a new user). The first load of an account still shows the loading
 * state, and a foreground re-check still does: ChatScreen's gates rely on it to wait
 * out the status after a turn that started the clock.
 */

export interface SubscriptionRefreshOptions {
  /** Refresh a status already on screen without showing the loading state. */
  background?: boolean;
}

/** True when this check should show the loading state. */
export function refreshShowsLoading(
  options: SubscriptionRefreshOptions | undefined,
  statusLoaded: boolean,
): boolean {
  return !(options?.background === true && statusLoaded);
}

/** True when a failed check should keep the status already on screen. */
export function refreshKeepsStatusOnError(
  options: SubscriptionRefreshOptions | undefined,
  statusLoaded: boolean,
): boolean {
  return options?.background === true && statusLoaded;
}

/**
 * The options a queued re-check runs with: background only if every request that
 * queued it asked for background — one foreground request makes it foreground.
 */
export function mergeQueuedRefresh(
  queued: SubscriptionRefreshOptions | null,
  incoming: SubscriptionRefreshOptions | undefined,
): SubscriptionRefreshOptions {
  const background = (queued === null || queued.background === true) && incoming?.background === true;
  return { background };
}
